import { CANNABINOID_ORDER, CANNABINOID_INFO } from "@/lib/cannabinoids";

export default function Learn() {
  const topics = [
    { t: "What is Hemp?", b: "Hemp is a variety of the Cannabis sativa plant containing very low levels of Delta-9 THC (less than 0.3% by dry weight). Federally legal under the 2018 Farm Bill, hemp is grown in the U.S. for its seeds and cannabinoids, which we use in edibles and drinks." },
    { t: "CBD (Cannabidiol)", b: "A naturally occurring, non-intoxicating cannabinoid found in hemp. In our shop it shows up in gummies, other edibles, and infused beverages — never anything you smoke." },
    { t: "Delta-9 (Hemp-Derived)", b: "Delta-9 THC is permitted in hemp-derived edibles and drinks when the finished product contains less than 0.3% by dry weight, per federal law. Effects vary by individual." },
    { t: "Functional Mushrooms", b: "Adaptogenic mushroom extracts — such as lion's mane, reishi, and cordyceps — used in wellness edibles and drinks. These are non-psychedelic, food-grade mushroom ingredients." },
    { t: "Full Spectrum vs Broad Spectrum vs Isolate", b: "Full spectrum products contain the full range of cannabinoids found in hemp (including trace Delta-9 THC). Broad spectrum products remove detectable THC. Isolate is a single, purified cannabinoid." },
    { t: "What is a COA?", b: "A Certificate of Analysis (COA) is a third-party lab report verifying cannabinoid content and screening for pesticides, heavy metals, residual solvents, and microbial contaminants. Every batch we sell has a COA on file." },
    { t: "How to Start", b: "Always start with the lowest possible serving and wait 60–90 minutes before considering more — edibles and drinks can take time to take effect. Individual tolerance varies. Do not use while operating vehicles or heavy machinery." },
    { t: "Texas Regulations", b: "Texas allows the sale of compliant hemp-derived products. In line with current state guidance, we sell only edibles, drinks, and mushroom products — no smokable hemp of any kind. We stay current with the law and adjust our shelves accordingly." },
  ];

  return (
    <div data-testid="learn-page" className="bg-sand">
      <section className="bg-white border-b border-tan/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <span className="eyebrow">Hemp Education</span>
          <h1 className="font-serif text-5xl sm:text-6xl text-navy mt-4 leading-[1.05]">A clear guide to <span className="italic text-copper">hemp products.</span></h1>
          <div className="divider my-6" />
          <p className="text-lg text-charcoal/85 leading-relaxed max-w-3xl font-light">
            A plain-spoken introduction to what these products are, what's in them, and how to think about quality.
            We do not make medical claims — this content is educational only.
          </p>
        </div>
      </section>

      {/* Cannabinoid guide — the same quick reports that pop up on the Shop page */}
      <section id="cannabinoids" data-testid="learn-cannabinoids" className="bg-sand/50 py-16 lg:py-20 border-b border-tan/30 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <span className="eyebrow">Know Your Cannabinoids</span>
          <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-3 leading-tight">A quick guide to what&apos;s inside.</h2>
          <div className="divider my-6" />
          <p className="text-base text-charcoal/85 leading-relaxed max-w-3xl mb-10">
            When you filter by a cannabinoid in our shop, this same short report pops up so you always know what you&apos;re buying.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {CANNABINOID_ORDER.map((key) => {
              const c = CANNABINOID_INFO[key];
              return (
                <article key={key} data-testid={`learn-cannabinoid-${key}`} className="bg-white border border-tan/30 p-7">
                  <div className="flex items-baseline justify-between gap-3">
                    <div>
                      <span className="font-serif text-3xl text-navy">{c.name}</span>
                      <span className="ml-2 text-sm text-charcoal/60 italic">{c.full}</span>
                    </div>
                    <span className="border border-copper/60 text-copper px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] whitespace-nowrap">{c.tag}</span>
                  </div>
                  <div className="divider my-4" />
                  <p className="text-base text-charcoal/90 leading-relaxed">{c.desc}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-sand/50 py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 space-y-12">
          {topics.map((t, i) => (
            <article key={i} data-testid={`learn-topic-${i}`} className="bg-white border border-tan/30 p-8 lg:p-10">
              <h2 className="font-serif text-3xl text-navy">{t.t}</h2>
              <div className="divider my-4" />
              <p className="text-base text-charcoal/90 leading-relaxed">{t.b}</p>
            </article>
          ))}

          <div className="bg-navy text-white p-8 mt-10">
            <div className="eyebrow text-tan">FDA Required Notice</div>
            <p className="mt-4 text-sm leading-relaxed text-white/80">
              These products have not been evaluated by the Food and Drug Administration.
              They are not intended to diagnose, treat, cure, or prevent any disease.
              Consult your physician before using if you are pregnant, nursing, or taking medication.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
